/**
 * AscMatrix — Addressable Strategic Challenges Scatter Plot
 *
 * Renders a scatter chart with:
 *   X-axis: Importance (1–10)
 *   Y-axis: Addressability (1–10)
 *   Top-right quadrant (6–10 × 6–10) highlighted as the "Crux zone"
 *   Click a point to set it as the Crux
 */

import { useCallback } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { StarIcon } from '@heroicons/react/24/solid';
import { StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import type { StrategyChallenge } from '../../../types/prioritization/strategyKernel';
import { isHighPriorityChallenge } from '../../../types/prioritization/strategyKernel';

// ============================================================================
// Custom tooltip
// ============================================================================

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: StrategyChallenge & { x: number; y: number } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg)] px-3 py-2 shadow-lg text-sm max-w-48">
      <p className="font-semibold text-[var(--text-pri)] leading-snug mb-1">{d.title}</p>
      <div className="flex gap-3 text-xs text-[var(--text-ter)]">
        <span>Importance: <strong className="text-[var(--text-ter)]">{d.importance}</strong></span>
        <span>Addressability: <strong className="text-[var(--text-ter)]">{d.addressability}</strong></span>
      </div>
      {d.isCrux && (
        <p className="mt-1 text-xs font-medium text-[var(--mustard)] flex items-center gap-1">
          <StarIcon className="w-3 h-3" /> Selected Crux
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Props
// ============================================================================

interface AscMatrixProps {
  challenges: StrategyChallenge[];
  onSelectCrux: (challengeId: string) => void;
  onClearCrux: () => void;
  readOnly?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function AscMatrix({ challenges, onSelectCrux, onClearCrux, readOnly = false }: AscMatrixProps) {
  const data = challenges.map(c => ({ ...c, x: c.importance, y: c.addressability }));

  const handleClick = useCallback(
    (point: { payload?: StrategyChallenge }) => {
      if (readOnly || !point.payload) return;
      const c = point.payload;
      if (c.isCrux) {
        onClearCrux();
      } else if (isHighPriorityChallenge(c)) {
        onSelectCrux(c.id);
      }
    },
    [readOnly, onSelectCrux, onClearCrux]
  );

  return (
    <div className="space-y-3">
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-[var(--text-ter)]">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-[var(--slate)]" />
          Challenge
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 rounded-full bg-[var(--mustard)]" />
          Crux (selected)
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-3 bg-[var(--green)]/40 border border-[var(--green)]/50" />
          Crux zone (High Importance + High Addressability)
        </span>
        {!readOnly && (
          <span className="text-[var(--text-ter)] italic">Click a point in the green zone to set as Crux</span>
        )}
      </div>

      <ResponsiveContainer width="100%" height={340}>
        <ScatterChart margin={{ top: 16, right: 24, bottom: 24, left: 8 }}>
          {/* Crux zone highlight (top-right quadrant) */}
          <ReferenceArea
            x1={5.5}
            x2={10.5}
            y1={5.5}
            y2={10.5}
            fill="#14532d"
            fillOpacity={0.3}
            stroke="#166534"
            strokeWidth={1}
          />

          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />

          <XAxis
            type="number"
            dataKey="x"
            domain={[0.5, 10.5]}
            ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            label={{ value: 'Importance', position: 'insideBottom', offset: -10, fontSize: 12, fill: '#9ca3af' }}
          />
          <YAxis
            type="number"
            dataKey="y"
            domain={[0.5, 10.5]}
            ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            label={{ value: 'Addressability', angle: -90, position: 'insideLeft', offset: 16, fontSize: 12, fill: '#9ca3af' }}
          />

          {/* Midpoint lines */}
          <ReferenceLine x={5.5} stroke="#4b5563" strokeDasharray="4 2" />
          <ReferenceLine y={5.5} stroke="#4b5563" strokeDasharray="4 2" />

          <Tooltip content={<CustomTooltip />} cursor={{ strokeDasharray: '3 3' }} />

          <Scatter
            data={data}
            onClick={handleClick}
            cursor={readOnly ? 'default' : 'pointer'}
          >
            {data.map(entry => (
              <Cell
                key={entry.id}
                fill={entry.isCrux ? '#f59e0b' : isHighPriorityChallenge(entry) ? '#3b82f6' : '#6b7280'}
                stroke={entry.isCrux ? '#d97706' : isHighPriorityChallenge(entry) ? '#2563eb' : '#4b5563'}
                strokeWidth={entry.isCrux ? 2 : 1}
                r={entry.isCrux ? 9 : 7}
              />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>

      {/* Challenge list below chart */}
      {challenges.length > 0 && (
        <div className="border border-[var(--rule)] divide-y divide-[var(--rule)] text-sm">
          {challenges.map(c => (
            <div key={c.id} className="flex items-center gap-3 px-3 py-2 bg-[var(--bg-raised)] first: last:">
              <div className="flex-1 min-w-0">
                <span className="font-medium text-[var(--text-ter)]">{c.title}</span>
                {c.description && (
                  <span className="ml-2 text-[var(--text-ter)] text-xs">{c.description}</span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-[var(--text-ter)] shrink-0">
                <span>I: <strong className="text-[var(--text-ter)]">{c.importance}</strong></span>
                <span>A: <strong className="text-[var(--text-ter)]">{c.addressability}</strong></span>
              </div>
              {!readOnly && (
                <button
                  onClick={() => c.isCrux ? onClearCrux() : isHighPriorityChallenge(c) ? onSelectCrux(c.id) : undefined}
                  disabled={!isHighPriorityChallenge(c) && !c.isCrux}
                  title={
                    c.isCrux
                      ? 'Clear as Crux'
                      : !isHighPriorityChallenge(c)
                      ? 'Must score 6+ on both axes to be eligible as Crux'
                      : 'Set as Crux'
                  }
                  className={`shrink-0 transition-colors ${
                    c.isCrux
                      ? 'text-[var(--mustard)] hover:text-[var(--mustard)]'
                      : isHighPriorityChallenge(c)
                      ? 'text-[var(--text-sec)] hover:text-[var(--text-ter)]'
                      : 'text-[var(--text-sec)] cursor-not-allowed'
                  }`}
                >
                  {c.isCrux ? (
                    <StarIcon className="w-4 h-4" />
                  ) : (
                    <StarOutlineIcon className="w-4 h-4" />
                  )}
                </button>
              )}
              {c.isCrux && (
                <span className="shrink-0 inline-flex items-center rounded-full bg-[var(--mustard)]/40 px-2 py-0.5 text-xs font-medium text-[var(--mustard)]">
                  Crux
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {challenges.length === 0 && (
        <div className="flex items-center justify-center h-20 text-sm text-[var(--text-ter)] border border-dashed border-[var(--rule)]">
          Add challenges above to populate the matrix.
        </div>
      )}
    </div>
  );
}
